import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core'
import { takeUntil, Subject, take, filter, combineLatest, map, switchMap, shareReplay, iif, startWith } from 'rxjs'
import {
  GIFT_SUBSCRIPTION_OFFERS_ANALYTICS_TAG,
  GiftSubscriptionAnalyticsService
} from 'src/app/gift-subscription/gift-subscription-analytics.service'
import {
  EXP_FLOGRAPPLING_INSTALLMENT_ID,
  EXP_FLOGRAPPLING_INSTALLMENT_VARIATION_1,
  EXP_FLOGRAPPLING_INSTALLMENT_VARIATION_2,
  FEAT_PRICING_ARCH_ID,
  FEAT_PRICING_ARCH_VARIATION
} from 'src/app/shared/experimentation/experiment.model'
import { ExperimentationService } from 'src/app/shared/experimentation/experimentation.service'
import { GiftService } from 'src/app/shared/services/gift.service'
import { FunnelRouteData, LEGACY_PLANS_SEGMENT_TAG, getFunnelProperties } from '../funnel-analytics.utility'
import { ActivatedRoute, Router } from '@angular/router'
import { SegmentService } from 'src/app/shared/analytics/services/segment.service'
import { AuthService } from 'src/app/singleton-services/auth/auth.service'
import { ProductService } from 'src/app/shared/services/product.service'
import { OffersService } from 'src/app/singleton-services/offers.service'
import { CheckoutNavigationService } from 'src/app/shared/services/checkout-navigation.service'
import { CouponStatusUtility } from '../coupon/coupon-status.utility'
import { trackById } from 'src/app/shared/utility-functions/track-by-id.utility'
import { getBillingPlanLabel } from './legacy-plans/legacy-plans.utility'
import {
  EXP_IMG_STYLE_STEPPER_ID,
  EXP_IMG_STYLE_STEPPER_VARIATION
} from '../../shared/experimentation/experiment.model'

const PICK_A_PLAN = 'Pick a Plan'
const JOIN_NOW = 'Join Now'

@Component({
  selector: 'flo-plans',
  templateUrl: './plans.component.html',
  styleUrls: ['./plans.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PlansComponent implements OnInit, OnDestroy {
  constructor(
    private readonly ar: ActivatedRoute,
    private readonly authService: AuthService,
    private readonly checkoutNavService: CheckoutNavigationService,
    private readonly couponStatusUtility: CouponStatusUtility,
    private readonly experimentService: ExperimentationService,
    private readonly giftService: GiftService,
    private readonly giftSubAnalytics: GiftSubscriptionAnalyticsService,
    private readonly offersService: OffersService,
    private readonly productService: ProductService,
    private readonly router: Router,
    private readonly segment: SegmentService
  ) {}

  // TODO: refactor to subscribe to the ngOnInit instead
  ngOnInit() {
    this.isInGiftSubscription$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(isInGiftSubscription => {
      if (isInGiftSubscription) {
        this.giftSubAnalytics.sendGiftPageCall(GIFT_SUBSCRIPTION_OFFERS_ANALYTICS_TAG, 'Gifter')
        return
      }
      this.sendPageCall()
      this.ensureLoggedIn()
    })
  }

  private readonly ngOnDestroy$ = new Subject<void>()
  public isInGiftSubscription$ = this.giftService.isInGiftSubscription$.pipe(shareReplay(1))
  public stepsText$ = this.checkoutNavService.plansStepsText$
  public isEmebeddedPurchase$ = this.ar.queryParams.pipe(map(queryParams => !!queryParams.embeddedPurchase))
  public isInNewPricingArchitectureVariation$ = this.experimentService.isInVariation(
    FEAT_PRICING_ARCH_ID,
    FEAT_PRICING_ARCH_VARIATION
  )
  // CXP-4854: FloGrappling Installment Experiment
  public isInInstallmentVariation1$ = this.experimentService.isInVariation(
    EXP_FLOGRAPPLING_INSTALLMENT_ID,
    EXP_FLOGRAPPLING_INSTALLMENT_VARIATION_1,
    true
  )

  // CXP-4854: FloGrappling Installment Experiment
  public isInInstallmentVariation2$ = this.experimentService.isInVariation(
    EXP_FLOGRAPPLING_INSTALLMENT_ID,
    EXP_FLOGRAPPLING_INSTALLMENT_VARIATION_2,
    true
  )

  public isVerticalLayout$ = combineLatest([this.isInInstallmentVariation1$, this.isInInstallmentVariation2$]).pipe(
    map(
      ([isInInstallmentVariation1, isInInstallmentVariation2]) => isInInstallmentVariation1 || isInInstallmentVariation2
    )
  )

  public title$ = this.isInNewPricingArchitectureVariation$.pipe(
    switchMap(isInNewPricingArchitectureVariation => {
      if (isInNewPricingArchitectureVariation) {
        return this.offersService.offers$.pipe(map(offer => (offer.length > 1 ? PICK_A_PLAN : JOIN_NOW)))
      } else {
        return this.productService.hasOneShownPlan$.pipe(
          map(hasOneShownPlan => (hasOneShownPlan ? JOIN_NOW : PICK_A_PLAN))
        )
      }
    })
  )

  // plan props change as the user selects a plan while on the old pricing architecture
  private legacyPlanProps$ = combineLatest([
    this.productService.savingsProp$,
    this.productService.selectedPlan$,
    this.productService.planProps$
  ]).pipe(
    map(([savingsProp, selectedPlan, planProps]) => {
      // TODO: create a utility function for this
      const savings = selectedPlan?.yearly ? [savingsProp] : [] // if it isn't yearly, don't show savings
      const billedProps = selectedPlan ? [getBillingPlanLabel(selectedPlan)] : []
      return [...savings, billedProps, ...planProps]
    })
  )

  // plan props change as the user selects a plan while on the new pricing architecture
  public newPlanProps$ = this.offersService.selectedOffer$.pipe(
    map(selectedOffer => selectedOffer?.bulletPoints.map(bullet => bullet.text))
  )

  // only displayed on mobile because the value props are dynamic upon selection
  public valueProps$ = this.isInNewPricingArchitectureVariation$.pipe(
    switchMap(isInNewPricingArchitectureVariation =>
      iif(() => isInNewPricingArchitectureVariation, this.newPlanProps$, this.legacyPlanProps$)
    )
  )

  public isInImageStyleStepperVariation$ = this.experimentService
    .isInVariation(EXP_IMG_STYLE_STEPPER_ID, EXP_IMG_STYLE_STEPPER_VARIATION, true)
    .pipe(startWith(false), shareReplay(1))

  public goToNextPage(): void {
    combineLatest([this.ar.queryParams, this.isInGiftSubscription$, this.authService.loggedIn$])
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(([queryParams, isInGiftSubscription, isLoggedIn]) => {
        this.couponStatusUtility.hideCouponErrorSource.next(true)
        // TODO: use checkoutNavigation service
        let urlPath
        if (isInGiftSubscription) {
          urlPath = '/gift/info'
        } else if (!isLoggedIn) {
          urlPath = '/create-account'
        } else {
          urlPath = '/pay'
        }
        this.router.navigate([urlPath], { queryParams })
      })
  }

  private sendPageCall(): void {
    const qp = this.ar.snapshot.queryParamMap
    const routeData = this.ar.snapshot.data as unknown as FunnelRouteData
    this.segment.pageV2(LEGACY_PLANS_SEGMENT_TAG, getFunnelProperties(routeData, qp))
  }

  private ensureLoggedIn() {
    this.ar.queryParams
      .pipe(
        take(1),
        filter(queryParams => !!queryParams.embeddedPurchase)
      )
      .subscribe(_ => {
        // this ensures the cookies are properly set across the whole domain coming from embedded purchase
        this.authService.refreshViaToken()
      })
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next()
    this.ngOnDestroy$.complete()
  }

  readonly trackById = trackById
}
